/**
 * CodeMirrorSQL — a controlled CodeMirror 6 editor pre-configured for SQL.
 *
 * Props:
 *   value        string          current content (controlled)
 *   onInput      (string) => void  called on every change
 *   onViewReady  (EditorView) => void  called once after mount with the raw view
 *   placeholder  string          shown when value is empty
 *   height       string          CSS height, default "280px"
 *   autoFormat   bool            when true, auto-format the statement you just
 *                                finished when you type a top-level ';' (opt-in;
 *                                pure-statement scripts only — DELIMITER/routine
 *                                scripts are skipped). On-demand Ctrl+Shift+F /
 *                                Shift+Alt+F formatting works regardless.
 *   bracketComplete  { connections(): [{id,name}], databases(name): Promise<string[]>,
 *                      tables?(name,db): Promise<string[]>, columns?(name,db,table): Promise<string[]> }
 *                                optional. Enables the Fleet/Bulk routing-prefix
 *                                completion: `[` → connection names, `[Conn].` →
 *                                databases, `[Conn].db.` → tables, and
 *                                `[Conn].db.table.` → columns (the last two only
 *                                when tables/columns providers are supplied).
 *   schema       () => ({schema, defaultSchema}) | null   optional reactive
 *                                accessor supplying the lang-sql completion
 *                                namespace (tables→columns) + the default schema
 *                                (current DB). When present, schema-aware
 *                                autocomplete is enabled; absent ⇒ keyword-only.
 *   triggerTable string          optional: the table a bare trigger body belongs
 *                                to (Table Editor), so `NEW.` / `OLD.` complete
 *                                its columns. A full CREATE TRIGGER … ON t is
 *                                detected from the text without it.
 *   onExpandColumns ({statementText, fromTables, insert}) => void  optional.
 *                                Fired by Ctrl+Shift+E. The parent opens the
 *                                column-expander modal; `insert(text)` writes the
 *                                generated column list at the cursor.
 *   readOnly     bool            when true, the editor is a read-only viewer
 *                                (no edits, no caret) — text stays selectable for
 *                                copy and keeps full syntax highlighting.
 *   maxHeight    string          when set, the editor auto-grows with its content
 *                                up to this CSS cap (overrides `height`); pairs
 *                                with `minHeight` (default "80px").
 *   lineWrap     bool            wrap long lines instead of scrolling sideways.
 *   autofocus    bool            focus the editor when this turns true — at mount
 *                                (a tab that opens already active) and whenever it
 *                                flips true again (the tab is re-activated).
 *   sessionVars  () => string[]   optional. Variable names offered FIRST when the
 *                                user types `@` (e.g. @vNinocatUser, @vOldFoo —
 *                                the form/import session vars). The routine's own
 *                                parameters + local DECLAREs are parsed from the
 *                                document and added automatically (shown on `@`,
 *                                inserted without it).
 */
import { onMount, onCleanup, createEffect } from 'solid-js';
import { EditorView, keymap, lineNumbers, drawSelection, highlightActiveLine, hoverTooltip, rectangularSelection, crosshairCursor, showPanel, showTooltip } from '@codemirror/view';
import { EditorState, EditorSelection, Compartment, Facet, StateField, StateEffect } from '@codemirror/state';
import { MySQL, schemaCompletionSource, keywordCompletionSource } from '@codemirror/lang-sql';
import { endsWithQualifier, qualifiedCompletions, visibleColumns } from '../lib/sqlqualify.js';
import { autocompletion, completionKeymap, acceptCompletion, snippetCompletion, startCompletion } from '@codemirror/autocomplete';
import { search, searchKeymap, selectNextOccurrence } from '@codemirror/search';
import { syntaxTree, bracketMatching, codeFolding, foldGutter, foldKeymap, LanguageSupport } from '@codemirror/language';
import { linter, forceLinting } from '@codemirror/lint';
import { history, defaultKeymap, historyKeymap, indentWithTab } from '@codemirror/commands';
import { theme, themeBase } from '../lib/theme.js';
import { editorTheme } from '../lib/cmtheme.js';
import { routineCompletionMode, fromTables, writeTargetTables, aliasContext, tableAlias } from '../lib/schema.js';
import { callContextAt, splitSigArgs } from '../lib/sqlsig.js';
import { buildIndex, warm as schemaWarm, joinConditions } from '../lib/schemaindex.js';
import { lintStatement } from '../lib/sqllint.js';
import { identAt, findOccurrences, renderName } from '../lib/sqlrename.js';
import { extractRoutineLocals } from '../lib/sqllocals.js';
import { formatSql, formatStatement, formatOneLine, formatRoutineIndent, isOpaqueStatement, recaseColumns } from '../lib/sqlformat.js';
import { editorFontSize, setEditorFontSize } from '../lib/prefs.js';
import { columnCaseMap } from '../lib/columnrename.js';
import { FUNCTIONS } from '../lib/sqlfunctions.js';
import { funcBoost, bumpFuncUsage } from '../lib/funcusage.js';
import { pasteCommaIntoView } from '../lib/pastecomma.js';
import { toast } from '../lib/toast.js';

// Facet carrying the format-time column re-casing inputs: { names, caseMap }.
// `names` is the lowercased set of known column names (schema-driven heuristic
// fallback); `caseMap` is the user's GLOBAL rename dictionary (lowercased→
// canonical, case-only) which takes priority. Provided per editor via a
// compartment (schema arrives / dictionary changes after mount); read by
// formatEditor / autoFormatOnSemicolon, which only receive the view.
const columnsFacet = Facet.define({ combine: (vals) => vals[0] || { names: null, caseMap: null } });

// Live templates ("snippets"): short mnemonics that expand to a SQL skeleton with
// ${placeholder} tab-stops (Tab cycles through them). Built-in set; user-defined
// snippets are a later follow-up. `${}`-less words are literal. The `apply` is
// produced by snippetCompletion at registration time (see SNIPPET_OPTIONS).
const SNIPPETS = [
  { label: 'sel', detail: 'SELECT … FROM … WHERE', template: 'SELECT ${columns}\nFROM ${table}\nWHERE ${condition}' },
  { label: 'sela', detail: 'SELECT * FROM …', template: 'SELECT * FROM ${table}' },
  { label: 'count', detail: 'SELECT COUNT(*) …', template: 'SELECT COUNT(*) FROM ${table} WHERE ${condition}' },
  { label: 'ins', detail: 'INSERT INTO …', template: 'INSERT INTO ${table} (${columns})\nVALUES (${values})' },
  { label: 'upd', detail: 'UPDATE … SET …', template: 'UPDATE ${table}\nSET ${col} = ${val}\nWHERE ${condition}' },
  { label: 'del', detail: 'DELETE FROM …', template: 'DELETE FROM ${table}\nWHERE ${condition}' },
  { label: 'joinon', detail: 'JOIN … ON …', template: 'JOIN ${table} ${alias} ON ${alias}.${col} = ${ref}' },
  { label: 'cte', detail: 'WITH … AS ( … )', template: 'WITH ${name} AS (\n  ${subquery}\n)\nSELECT * FROM ${name}' },
  { label: 'case', detail: 'CASE WHEN … END', template: 'CASE WHEN ${cond} THEN ${a} ELSE ${b} END' },
  // Ninocat's batch-generation DSL (documented in the 📖 DSL toolbar help):
  // src runs first, then target is emitted once per src row with &Col substituted.
  { label: 'expand', detail: 'one statement per src row (&Col macros)', template: 'expand src="${src query}", target="${target with &Col}"' },
];
const SNIPPET_OPTIONS = SNIPPETS.map((s) =>
  snippetCompletion(s.template, { label: s.label, detail: s.detail, type: 'snippet', boost: 50 }));

// MySQL built-in function completions (DATE_FORMAT, DATE_SUB, NOW, …), available
// in EVERY CodeMirror editor. Ranked by the user's personal pick frequency
// (lib/funcusage): the boost is refreshed live from the frequency map, so among
// equal text matches the function you pick most ranks highest. Static option
// objects are built once and reused — only their boost changes per call.
// Inserts `NAME(` (caret inside the parens), `NAME()` for no-arg functions, or
// the bare name for pseudo-constants (CURRENT_TIMESTAMP). Picking one bumps it.
const FUNCTION_OPTIONS = FUNCTIONS.map((f) => ({
  label: f.name,
  type: 'function',
  detail: f.sig,
  boost: 0,
  apply: (view, _completion, from, to) => {
    const insert = f.kind === 'bare' ? f.name : f.kind === 'noargs' ? `${f.name}()` : `${f.name}(`;
    view.dispatch({ changes: { from, to, insert }, selection: { anchor: from + insert.length } });
    bumpFuncUsage(f.name);
  },
}));
function functionSource(context) {
  const word = context.matchBefore(/[\w]*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;
  const before = context.state.sliceDoc(Math.max(0, word.from - 4000), word.from);
  if (/\.\s*$/.test(before) || before.endsWith('@')) return null; // qualified (x.y) / @var → not here
  if (routineCompletionMode(before) !== 'function') return null;  // expression positions only
  for (const o of FUNCTION_OPTIONS) o.boost = funcBoost(o.label);
  return { from: word.from, options: FUNCTION_OPTIONS, validFor: /^[\w]*$/ };
}

// ── In-editor rename (F2) ───────────────────────────────────────────────────
// Occurrence-rename the identifier at the caret across the WHOLE buffer (not the
// database — that's the Table Editor's job). Quote/comment/string-aware via the
// shared tokenizer; one undoable transaction. A small showPanel bar holds the
// input + live occurrence count.
const setRename = StateEffect.define(); // payload: {name, count} | null
const renameField = StateField.define({
  create: () => null,
  update(val, tr) {
    for (const e of tr.effects) if (e.is(setRename)) val = e.value;
    if (val && tr.docChanged) val = null; // ranges would be stale → close
    return val;
  },
  provide: (f) => showPanel.from(f, (val) => (val ? renamePanel(val) : null)),
});

function applyRename(view, originalName, newName) {
  newName = (newName || '').trim();
  view.dispatch({ effects: setRename.of(null) });
  if (!newName || newName === originalName) { view.focus(); return; }
  // Recompute against the live doc (belt-and-suspenders; doc is frozen while the
  // panel is open, but the field also auto-closes on any doc change).
  const occ = findOccurrences(view.state.doc.toString(), originalName);
  if (!occ.length) { view.focus(); return; }
  const changes = occ.map((o) => ({ from: o.from, to: o.to, insert: renderName(newName, o.quoted) }));
  view.dispatch({ changes });
  view.focus();
}

function renamePanel(state) {
  return (view) => {
    const dom = document.createElement('div');
    dom.className = 'cm-rename-bar';
    const label = document.createElement('span');
    label.className = 'cm-rename-label';
    label.textContent = 'Rename to:';
    const input = document.createElement('input');
    input.className = 'input cm-rename-input';
    input.value = state.name;
    const count = document.createElement('span');
    count.className = 'cm-rename-count muted';
    count.textContent = `${state.count} occurrence${state.count === 1 ? '' : 's'} · Enter to apply · Esc to cancel`;
    input.addEventListener('keydown', (e) => {
      e.stopPropagation(); // keep CM keymaps out of the input
      if (e.key === 'Enter') { e.preventDefault(); applyRename(view, state.name, input.value); }
      else if (e.key === 'Escape') { e.preventDefault(); view.dispatch({ effects: setRename.of(null) }); view.focus(); }
    });
    dom.append(label, input, count);
    return {
      dom, top: true,
      mount() { queueMicrotask(() => { input.focus(); input.select(); }); },
    };
  };
}

function startRename(view) {
  if (view.state.readOnly) return false;
  const sel = view.state.selection.main;
  const text = view.state.doc.toString();
  let name = null;
  if (!sel.empty) {
    const w = view.state.sliceDoc(sel.from, sel.to).trim().replace(/`/g, '');
    if (/^[A-Za-z_$][\w$]*$/.test(w)) name = w;
  } else {
    name = identAt(text, sel.head)?.name || null;
  }
  if (!name) return false;
  const occ = findOccurrences(text, name);
  if (!occ.length) return false;
  view.dispatch({ effects: setRename.of({ name, count: occ.length }) });
  return true;
}

// Build the column-name set (lowercased) from the lang-sql schema namespace
// {db: {table: [colCompletions]}}; each completion's `label` is the column name.
function columnSetFrom(cfg) {
  const set = new Set();
  const ns = cfg && cfg.schema;
  if (ns) for (const db of Object.values(ns)) {
    if (db) for (const cols of Object.values(db)) {
      if (Array.isArray(cols)) for (const c of cols) if (c && c.label) set.add(String(c.label).toLowerCase());
    }
  }
  return set;
}
import { statementAtCursor, splitStatements } from '../lib/sqlsplit.js';

// Fixed formatting options (keyword case is UPPERCASE by product decision).
// FULL: multi-line reflow (button default + Ctrl+Shift+F). CASE: keyword-case
// only, nothing else touched ("Change case only" menu item). AUTO (on ';'):
// uppercase + tidy spacing, your line breaks preserved.
const FORMAT_OPTS = { keywordCase: 'upper', indent: '  ' };
const CASE_OPTS   = { keywordCase: 'upper', mode: 'case' };
const AUTO_OPTS   = { keywordCase: 'upper', lineBreaks: false };

// Editor theme, swapped by the app theme (see themeConf below): one var-driven
// chrome + syntax palette (lib/cmtheme.js) that tracks every app theme; only
// CodeMirror's light/dark defaults follow the theme's base.
const editorThemeExt = () => editorTheme(themeBase());

// Smallest from/to/insert that turns oldStr into newStr, so the editor's
// selection maps cleanly through the change — the cursor stays put when the edit
// is localised (e.g. auto-format of just the statement you finished).
function minimalChange(oldStr, newStr, base) {
  const oLen = oldStr.length, nLen = newStr.length;
  let s = 0; const maxS = Math.min(oLen, nLen);
  while (s < maxS && oldStr.charCodeAt(s) === newStr.charCodeAt(s)) s++;
  let e = 0; const maxE = Math.min(oLen - s, nLen - s);
  while (e < maxE && oldStr.charCodeAt(oLen - 1 - e) === newStr.charCodeAt(nLen - 1 - e)) e++;
  return { from: base + s, to: base + oLen - e, insert: newStr.slice(s, nLen - e) };
}

// Format the selection if there is one, else the whole document. Dispatched as a
// minimal-diff transaction (preserves undo + remaps the selection) — NOT by
// resetting the controlled `value`, which would jump the cursor to position 0.
// Exported so a toolbar control can run it on the view from onViewReady.
// mode: 'full' (default, multi-line reflow) | 'case' (keyword-case only) |
//       'oneline' (collapse each statement onto its own single line) |
//       'indent' (re-indent a stored-routine body: BEGIN/IF/ELSE/loops).
export function formatEditor(view, mode = 'full') {
  if (mode === 'oneline') return oneLineEditor(view);
  if (mode === 'indent') return indentRoutineEditor(view);
  const opts = mode === 'case' ? CASE_OPTS : FORMAT_OPTS;
  const sel = view.state.selection.main;
  const hasSel = sel.from !== sel.to;
  const base = hasSel ? sel.from : 0;
  const text = hasSel ? view.state.sliceDoc(sel.from, sel.to) : view.state.doc.toString();
  // Re-case recognised column names first (a no-op when the schema isn't loaded),
  // then run the whitespace/keyword formatter on the already-recased text.
  const fv = view.state.facet(columnsFacet) || {};
  const out = formatSql(recaseColumns(text, fv.names, fv.caseMap), opts);
  if (out !== text) view.dispatch({ changes: minimalChange(text, out, base), scrollIntoView: true });
  view.focus();
  return true;
}

// Single line PER STATEMENT: collapse each ';'-separated statement onto its own
// line. Operates on the selection if there is one, else the whole document (same
// as the full/case formatters). formatOneLine leaves routine bodies + line-comment
// statements untouched and reports how many it skipped, so we can toast why.
function oneLineEditor(view) {
  const sel = view.state.selection.main;
  const hasSel = sel.from !== sel.to;
  const base = hasSel ? sel.from : 0;
  const text = hasSel ? view.state.sliceDoc(sel.from, sel.to) : view.state.doc.toString();
  const { text: out, skipped } = formatOneLine(text, { keywordCase: 'upper' });
  if (out !== text) view.dispatch({ changes: minimalChange(text, out, base), scrollIntoView: true });
  if (skipped > 0) {
    toast.info(`Left ${skipped} statement${skipped > 1 ? 's' : ''} as-is — line comments and routine bodies can't be single-lined.`);
  }
  view.focus();
  return true;
}

// Re-indent a stored-routine body (BEGIN/IF/ELSE/WHILE/LOOP/REPEAT/CASE),
// preserving its line breaks. Operates on the selection if there is one, else the
// whole document. Toasts when there's no routine body to indent.
function indentRoutineEditor(view) {
  const sel = view.state.selection.main;
  const hasSel = sel.from !== sel.to;
  const base = hasSel ? sel.from : 0;
  const text = hasSel ? view.state.sliceDoc(sel.from, sel.to) : view.state.doc.toString();
  const { text: out, routines } = formatRoutineIndent(text, { keywordCase: 'upper' });
  if (routines === 0) { toast.info('No routine body (BEGIN…END / IF / loop) found to indent.'); view.focus(); return true; }
  if (out !== text) view.dispatch({ changes: minimalChange(text, out, base), scrollIntoView: true });
  view.focus();
  return true;
}

// ── Open-the-dropdown-on-space ────────────────────────────────────────────────
// After a space, auto-open the completion popup ONLY when the space begins a new
// operand / table reference — so you can browse columns/functions without typing
// a letter first, but it doesn't pop up after every space. The trigger fires the
// SAME explicit completion as Ctrl-Space; the existing sources decide what to
// show (columns boosted above functions; tables in FROM/JOIN slots).
const SPACE_OPEN_KW = new Set([
  // expression starters
  'WHERE','AND','OR','XOR','NOT','HAVING','ON','SET','SELECT','BY','VALUES','VALUE',
  'WHEN','THEN','ELSE','CALL','RETURN','USING','DISTINCT',
  // table-reference starters
  'FROM','JOIN','INTO','UPDATE',
]);
function shouldOpenOnSpace(before) {
  const b = before.replace(/[ \t]+$/, '');
  if (!b) return false;
  const last = b[b.length - 1];
  if (last === '(' || last === ',') return true;           // operand after ( or ,
  if ('=<>!+-/%'.includes(last)) return true;              // comparison/arith ('*' excluded → not SELECT *)
  const m = b.match(/([A-Za-z_$][\w$]*)$/);                // a bare word right before the space
  return !!m && SPACE_OPEN_KW.has(m[1].toUpperCase());     // …and it's a clause keyword
}
function maybeOpenOnSpace(view) {
  const head = view.state.selection.main.head;
  // The space sits at head-1; never open inside a string/comment/quoted ident.
  const node = syntaxTree(view.state).resolveInner(Math.max(0, head - 1), -1);
  if (/string|comment|quoted/i.test(node.name)) return;
  if (shouldOpenOnSpace(view.state.sliceDoc(Math.max(0, head - 256), head - 1))) startCompletion(view);
}

// Ctrl/Cmd+D: duplicate the current line (or the block of lines the selection
// spans) directly below, and move the caret to the start (column 0) of the new
// copy. One transaction: insert "\n"+block at the line end, then anchor the
// caret just past the inserted newline.
function duplicateLineDown(view) {
  if (view.state.readOnly) return false;
  const { state } = view;
  const range = state.selection.main;
  const startLine = state.doc.lineAt(range.from);
  const endLine = state.doc.lineAt(range.to);
  const block = state.doc.sliceString(startLine.from, endLine.to);
  const at = endLine.to;
  view.dispatch({
    changes: { from: at, insert: '\n' + block },
    selection: { anchor: at + 1 }, // start of the inserted copy (column 0)
    scrollIntoView: true,
    userEvent: 'input.copyline',
  });
  return true;
}

// Procedural scripts are formatted only on demand (formatSql handles DELIMITER
// + routine opacity there); the auto path bails out of them entirely.
const HAS_DELIM = /(^|\n)\s*DELIMITER\s+\S/i;

function autoFormatOnSemicolon(view) {
  const doc = view.state.doc.toString();
  if (HAS_DELIM.test(doc)) return;
  const head = view.state.selection.main.head;
  const stmt = statementAtCursor(doc, head - 1); // the typed ';' sits at head-1
  if (!stmt) return;
  const raw = doc.slice(stmt.start, stmt.end); // [start,end) excludes the ';'
  const body = raw.trim();
  if (!body || isOpaqueStatement(body)) return;
  const fv = view.state.facet(columnsFacet) || {};
  const formatted = formatStatement(recaseColumns(body, fv.names, fv.caseMap), AUTO_OPTS);
  if (formatted === body) return;
  const bodyStart = stmt.start + (raw.length - raw.trimStart().length);
  view.dispatch({ changes: minimalChange(body, formatted, bodyStart) });
}

// Build the lang-sql extension, optionally schema-aware. Keywords are UPPER-cased
// on completion to match the formatter's house style.
// lang-sql supplies keywords plus UNQUALIFIED schema names (tables, databases,
// default-db columns). Every qualified `x.` / `x.y.` position is left to
// qualifiedSource (lib/sqlqualify): lang-sql's own is case-sensitive and only
// knows aliases from a statement's top-level FROM, so it missed subqueries,
// UPDATE aliases, derived tables, NEW./OLD. and `TM_BARANG.`.
// (Keywords are muted there too: `b.Na` should offer NamaBrg, not NATURAL.)
const unqualifiedOnly = (src) => (context) => {
  const head = context.state.sliceDoc(Math.max(0, context.pos - 300), context.pos);
  return endsWithQualifier(head) ? null : src(context);
};
function sqlExtension(schemaCfg) {
  // What lang-sql's sql() assembles, with both of its sources gated.
  const ext = [MySQL.language.data.of({ autocomplete: unqualifiedOnly(keywordCompletionSource(MySQL, true)) })];
  if (schemaCfg && schemaCfg.schema) {
    ext.push(MySQL.language.data.of({
      autocomplete: unqualifiedOnly(schemaCompletionSource({
        dialect: MySQL, schema: schemaCfg.schema,
        defaultSchema: schemaCfg.defaultSchema || undefined,
      })),
    }));
  }
  return new LanguageSupport(MySQL.language, ext);
}

// Pre-build the function/procedure snippet completions from the schema's routine
// list, split by kind. Functions are de-boosted so a table's columns (offered by
// columnSource with a positive boost) rank above them in expression positions.
function routineCompletions(routines) {
  const fn = [], pr = [];
  for (const r of (routines || [])) {
    const opt = snippetCompletion(r.snippet, {
      label: r.label, detail: r.detail, info: r.info,
      type: r.kind === 'PROCEDURE' ? 'method' : 'function',
      boost: r.kind === 'PROCEDURE' ? 1 : -1,
    });
    (r.kind === 'PROCEDURE' ? pr : fn).push(opt);
  }
  return { fn, pr };
}

export default function CodeMirrorSQL(props) {
  let container;
  let view;
  // Compartment so the SQL config (and its completion schema) can be swapped
  // when the schema loads / the current database changes — without rebuilding
  // the editor.
  const sqlConf = new Compartment();
  // Compartment carrying the known-column set into the columnsFacet, reconfigured
  // when the schema arrives/changes so the format-time re-caser sees real columns.
  const colsConf = new Compartment();
  // Separate compartment for line wrapping so a toolbar toggle can flip it live.
  const wrapConf = new Compartment();
  const wrapExt = () => (props.lineWrap ? [EditorView.lineWrapping] : []);
  // Font size lives in its own compartment so Ctrl/Cmd+wheel can zoom it live.
  // Backed by the shared `editor.fontSize` pref, so the size persists and every
  // CodeMirrorSQL instance stays in sync (mirrors editorLineWrap).
  const fontConf = new Compartment();
  const fontExt = () => EditorView.theme({ '&': { fontSize: editorFontSize() + 'px' } });
  // Editor theme (dark/light) lives in its own compartment, swapped live when the
  // app theme changes — same pattern as the font compartment.
  const themeConf = new Compartment();
  // Tracked flag for the hover tooltip (the source is built once at mount).
  let latestLineWrap = !!props.lineWrap;

  // When wrapping is OFF, hovering a line that's clipped by the viewport shows
  // the whole line in a wrapped tooltip — so long statements are readable
  // without horizontal scrolling. Skipped when wrapping is on (nothing hidden)
  // or the line fits. Char-width estimate is exact enough for the mono font.
  const lineHoverTooltip = hoverTooltip((view, pos) => {
    if (latestLineWrap) return null;
    const line = view.state.doc.lineAt(pos);
    const text = line.text;
    if (!text.trim()) return null;
    const charW = view.defaultCharacterWidth || 7;
    if (text.length * charW <= view.scrollDOM.clientWidth) return null; // not clipped
    return {
      pos: line.from,
      end: line.to,
      above: true,
      create: () => {
        const dom = document.createElement('div');
        dom.className = 'cm-line-tooltip';
        dom.textContent = text;
        return { dom };
      },
    };
  }, { hoverTime: 250 });

  // Hovering a stored function / procedure name shows its parameter signature
  // (same text the autocomplete shows as detail). Reads the live routine list.
  const routineHoverTooltip = hoverTooltip((view, pos) => {
    const routines = latestSchemaCfg?.routines;
    if (!routines || !routines.length) return null;
    const w = view.state.wordAt(pos);
    if (!w) return null;
    const name = view.state.sliceDoc(w.from, w.to).toLowerCase();
    const r = routines.find((x) => x.label.toLowerCase() === name);
    if (!r) return null;
    return {
      pos: w.from,
      end: w.to,
      above: true,
      create: () => {
        const dom = document.createElement('div');
        dom.className = 'cm-routine-tooltip mono';
        dom.textContent = r.info || (r.label + (r.detail || ''));
        return { dom };
      },
    };
  }, { hoverTime: 300 });

  // ── Hover quick-doc (tables + columns) ──────────────────────────────────────
  // Built entirely from the already-loaded schema payload (no extra fetch).
  // Tables → kind + column count + PK + a column preview; columns → type + PK/FK
  // badges, resolved against the statement's FROM/JOIN tables and `alias.col` /
  // `table.col` qualifiers. Routine names are left to routineHoverTooltip above.
  const eqi = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
  const tableMetaByName = (name, dbName) => {
    const dbs = latestSchemaCfg?.databases || [];
    const def = latestSchemaCfg?.defaultSchema;
    const order = dbName ? dbs.filter((d) => eqi(d.database, dbName))
      : [...dbs.filter((d) => eqi(d.database, def)), ...dbs.filter((d) => !eqi(d.database, def))];
    for (const d of order) {
      const isTable = (d.tables || []).find((x) => eqi(x.name, name));
      const t = isTable || (d.views || []).find((x) => eqi(x.name, name));
      if (t) return { db: d.database, table: t, isView: !isTable };
    }
    return null;
  };
  const isRoutineName = (name) => (latestSchemaCfg?.routines || []).some((r) => eqi(r.label, name));
  const identBeforeDot = (state, dotPos) => {
    let j = dotPos - 1;
    while (j >= 0 && /[\w$`]/.test(state.sliceDoc(j, j + 1))) j--;
    return state.sliceDoc(j + 1, dotPos).replace(/`/g, '');
  };
  const badgesFor = (meta, col) => {
    const out = [];
    if ((meta.table.keys || []).some((k) => eqi(k, col.name))) out.push({ cls: 'pk', text: 'PK' });
    const fk = (meta.table.foreignKeys || []).find((f) => (f.columns || []).some((c) => eqi(c, col.name)));
    if (fk) out.push({ cls: 'fk', text: `FK → ${fk.refTable}` });
    return out;
  };
  const colDocDom = (col, badges, ctxText) => {
    const dom = document.createElement('div'); dom.className = 'cm-quickdoc';
    const head = document.createElement('div'); head.className = 'cm-qd-head';
    const n = document.createElement('span'); n.className = 'cm-qd-name'; n.textContent = col.name; head.appendChild(n);
    if (col.type) { const t = document.createElement('span'); t.className = 'cm-qd-type'; t.textContent = col.type; head.appendChild(t); }
    for (const b of badges) { const e = document.createElement('span'); e.className = `cm-qd-badge cm-qd-${b.cls}`; e.textContent = b.text; head.appendChild(e); }
    dom.appendChild(head);
    if (ctxText) { const c = document.createElement('div'); c.className = 'cm-qd-sub'; c.textContent = ctxText; dom.appendChild(c); }
    return dom;
  };
  const tableDocDom = (meta) => {
    const cols = meta.table.columns || [];
    const pk = new Set((meta.table.keys || []).map((s) => String(s).toLowerCase()));
    const dom = document.createElement('div'); dom.className = 'cm-quickdoc';
    const head = document.createElement('div'); head.className = 'cm-qd-head';
    const n = document.createElement('span'); n.className = 'cm-qd-name'; n.textContent = meta.table.name; head.appendChild(n);
    const k = document.createElement('span'); k.className = 'cm-qd-badge cm-qd-kind'; k.textContent = meta.isView ? 'view' : 'table'; head.appendChild(k);
    dom.appendChild(head);
    const sub = document.createElement('div'); sub.className = 'cm-qd-sub';
    sub.textContent = `${cols.length} column${cols.length === 1 ? '' : 's'} · ${meta.db}` + (pk.size ? ` · PK: ${[...pk].join(', ')}` : '');
    dom.appendChild(sub);
    const list = document.createElement('div'); list.className = 'cm-qd-cols';
    for (const c of cols.slice(0, 12)) {
      const row = document.createElement('div'); row.className = 'cm-qd-col';
      const cn = document.createElement('span'); cn.className = 'cm-qd-col-n'; cn.textContent = (pk.has(c.name.toLowerCase()) ? '🔑 ' : '') + c.name;
      const ct = document.createElement('span'); ct.className = 'cm-qd-col-t'; ct.textContent = c.type || '';
      row.append(cn, ct); list.appendChild(row);
    }
    if (cols.length > 12) { const m = document.createElement('div'); m.className = 'cm-qd-more'; m.textContent = `+${cols.length - 12} more`; list.appendChild(m); }
    dom.appendChild(list);
    return dom;
  };
  const schemaHoverTooltip = hoverTooltip((view, pos) => {
    const cfg = latestSchemaCfg;
    if (!cfg || !cfg.databases) return null;
    const w = view.state.wordAt(pos);
    if (!w) return null;
    const word = view.state.sliceDoc(w.from, w.to);
    if (!/^[A-Za-z_$][\w$]*$/.test(word) || isRoutineName(word)) return null;
    const state = view.state;
    const mk = (dom) => (dom ? { pos: w.from, end: w.to, above: true, create: () => ({ dom }) } : null);
    const stmt = statementAtCursor(state.doc.toString(), pos);
    const refs = stmt ? [...fromTables(stmt.text), ...writeTargetTables(stmt.text)] : [];

    // qualified  x.word  (x = alias or table)
    if (w.from > 0 && state.sliceDoc(w.from - 1, w.from) === '.') {
      const qual = identBeforeDot(state, w.from - 1);
      if (!qual) return null;
      const ref = refs.find((r) => r.alias && eqi(r.alias, qual)) || refs.find((r) => eqi(r.table, qual));
      const meta = ref ? tableMetaByName(ref.table, ref.db) : tableMetaByName(qual);
      const col = meta && (meta.table.columns || []).find((c) => eqi(c.name, word));
      return col ? mk(colDocDom(col, badgesFor(meta, col), `column of ${meta.table.name}`)) : null;
    }

    // unqualified — alias?
    const aliasRef = refs.find((r) => r.alias && eqi(r.alias, word));
    if (aliasRef) { const m = tableMetaByName(aliasRef.table, aliasRef.db); if (m) return mk(tableDocDom(m)); }
    // column among the statement's tables?
    const hits = [];
    for (const r of refs) {
      const m = tableMetaByName(r.table, r.db);
      const col = m && (m.table.columns || []).find((c) => eqi(c.name, word));
      if (col) hits.push({ m, col });
    }
    if (hits.length) {
      const { m, col } = hits[0];
      const ctx = hits.length > 1 ? `column · in ${hits.map((h) => h.m.table.name).join(', ')}` : `column of ${m.table.name}`;
      return mk(colDocDom(col, badgesFor(m, col), ctx));
    }
    // a table name in the schema?
    const tmeta = tableMetaByName(word);
    return tmeta ? mk(tableDocDom(tmeta)) : null;
  }, { hoverTime: 300 });

  // ── Signature help ──────────────────────────────────────────────────────────
  // While the cursor is inside a function/procedure call, show its signature with
  // the current argument bolded. Sources: the built-in FUNCTIONS catalogue and
  // the live routine list. callContextAt (lib/sqlsig, node-tested) finds the call
  // + arg index; an unknown name simply yields no tooltip.
  const lookupSig = (name) => {
    const fn = FUNCTIONS.find((f) => eqi(f.name, name));
    if (fn) return fn.sig;
    const r = (latestSchemaCfg?.routines || []).find((x) => eqi(x.label, name));
    return r ? (r.info || (r.label + (r.detail || ''))) : null;
  };
  const sigTooltipDom = (raw, argIndex) => {
    const { name, args, trailing } = splitSigArgs(raw);
    const dom = document.createElement('div'); dom.className = 'cm-sighelp mono';
    const nm = document.createElement('span'); nm.className = 'cm-sig-name'; nm.textContent = name; dom.appendChild(nm);
    dom.appendChild(document.createTextNode('('));
    const active = Math.min(argIndex, args.length - 1);
    args.forEach((a, i) => {
      if (i > 0) dom.appendChild(document.createTextNode(', '));
      const s = document.createElement('span'); s.textContent = a;
      if (i === active) s.className = 'cm-sig-active';
      dom.appendChild(s);
    });
    dom.appendChild(document.createTextNode(')'));
    if (trailing && trailing.trim()) { const t = document.createElement('span'); t.className = 'cm-sig-ret'; t.textContent = trailing; dom.appendChild(t); }
    return dom;
  };
  const computeSig = (state) => {
    const sel = state.selection.main;
    if (!sel.empty) return null;
    const pos = sel.head;
    const before = state.sliceDoc(Math.max(0, pos - 2000), pos);
    if (!before.includes('(')) return null;                 // cheap early-out
    const ctx = callContextAt(before);
    if (!ctx) return null;
    const raw = lookupSig(ctx.name);
    if (!raw) return null;
    return { pos, above: true, create: () => ({ dom: sigTooltipDom(raw, ctx.argIndex) }) };
  };
  const sigHelpField = StateField.define({
    create: (state) => computeSig(state),
    update: (val, tr) => (tr.docChanged || tr.selection ? computeSig(tr.state) : val),
    provide: (f) => showTooltip.from(f),
  });

  // Latest schema config + routine completions (updated reactively below). These
  // sources close over them and the live document to stay context-aware. They
  // only fire on an UNQUALIFIED word — a qualified `x.y` is left to lang-sql's
  // own table.column / db.table completion.
  let latestSchemaCfg = props.schema?.();
  let latestColumnSet = columnSetFrom(latestSchemaCfg);
  let latestRoutineOpts = routineCompletions(latestSchemaCfg?.routines);
  // Derived FK/known-table index + warm-cache flag for JOIN completion + the
  // linter (built from the raw `databases` payload, not the lang-sql namespace).
  let latestIndex = buildIndex(latestSchemaCfg?.databases);
  let latestWarm = schemaWarm(latestSchemaCfg);

  // Conservative schema linter — only runs on a warm cache (so a cold/partial
  // schema never squiggles), and never on a DELIMITER/routine script (the
  // pure linter assumes plain statements). Re-runs on edits (debounced) + on
  // schema arrival (forceLinting in the schema effect).
  const sqlLinter = linter((view) => {
    if (!latestWarm || !latestIndex) return [];
    const doc = view.state.doc.toString();
    if (/^\s*delimiter\b/im.test(doc)) return [];
    const defaultDb = latestSchemaCfg?.defaultSchema;
    const out = [];
    for (const stmt of splitStatements(doc)) {
      for (const d of lintStatement(stmt.text, latestIndex, defaultDb)) {
        out.push({ from: stmt.start + d.from, to: stmt.start + d.to, severity: d.severity, message: d.message });
      }
    }
    return out;
  }, { delay: 400 });
  // The columnsFacet value for the current schema + the given rename dictionary.
  const colsFacetFor = (caseMap) => columnsFacet.of({ names: latestColumnSet, caseMap });

  const routineSource = (context) => {
    const opts = latestRoutineOpts;
    if (!opts) return null;
    const word = context.matchBefore(/[\w]*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    const before = context.state.sliceDoc(Math.max(0, word.from - 4000), word.from);
    if (/\.\s*$/.test(before) || before.endsWith('@')) return null; // qualified (x.y) or @var → not here
    const mode = routineCompletionMode(before);
    if (mode === 'none') return null;
    const options = mode === 'procedure' ? opts.pr : opts.fn;
    if (!options.length) return null;
    return { from: word.from, options, validFor: /^[\w]*$/ };
  };

  // Live-template snippets (sel → SELECT …). Fire on a non-qualified word (same
  // gate as the schema sources — never after a `.` or `@`), so they list
  // alongside completions but stay out of qualified/var slots. Modest boost so an
  // exact mnemonic ranks near the top without burying real column matches.
  const snippetSource = (context) => {
    const word = context.matchBefore(/[\w]*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    const before = context.state.sliceDoc(Math.max(0, word.from - 2), word.from);
    if (/[.@]\s*$/.test(before)) return null; // qualified (x.y) or @var → not here
    return { from: word.from, options: SNIPPET_OPTIONS, validFor: /^[\w]*$/ };
  };

  // In an expression position (WHERE/SELECT/SET/…), offer the columns of the
  // tables in the statement's FROM/JOIN clause, boosted so they rank ABOVE
  // functions and stray table names. This fills lang-sql's gap (it doesn't
  // reliably complete unqualified FROM-table columns).
  const columnSource = (context) => {
    const cfg = latestSchemaCfg;
    if (!cfg || !cfg.schema) return null;
    const word = context.matchBefore(/[\w]*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    const before = context.state.sliceDoc(Math.max(0, word.from - 4000), word.from);
    if (/\.\s*$/.test(before) || before.endsWith('@')) return null; // qualified (x.y) or @var → not here
    if (routineCompletionMode(before) !== 'function') return null; // expression positions only
    const at = stmtAt(context);
    if (!at) return null;
    // The tables in scope at the cursor — FROM/JOIN, UPDATE / INSERT targets,
    // derived tables and CTEs — the enclosing subquery's own first (boosted
    // highest), then each outer query's. Case-insensitive table lookup.
    const options = visibleColumns({ stmtText: at.text, cursor: at.cursor, ns: cfg.schema, defaultSchema: cfg.defaultSchema });
    if (!options.length) return null;
    return { from: word.from, options, validFor: /^[\w]*$/ };
  };

  // The statement around the cursor as RAW document text (leading whitespace
  // kept, so offsets line up) + the cursor's offset inside it.
  const stmtAt = (context) => {
    const doc = context.state.doc.toString();
    const stmt = statementAtCursor(doc, context.pos);
    if (!stmt) return null;
    const end = Math.max(stmt.end, context.pos);
    return { doc, text: doc.slice(stmt.start, end), cursor: context.pos - stmt.start, start: stmt.start };
  };

  // Every qualified position — `alias.`, `table.`, `db.`, `db.table.`, a
  // derived-table / CTE alias, `NEW.` / `OLD.` in a trigger — resolved scope-
  // aware and case-insensitively by lib/sqlqualify (node-tested). lang-sql's
  // schema source is muted at these positions (see sqlExtension).
  const qualifiedSource = (context) => {
    const cfg = latestSchemaCfg;
    if (!cfg || !cfg.schema) return null;
    const head = context.state.sliceDoc(Math.max(0, context.pos - 300), context.pos);
    if (!endsWithQualifier(head)) return null; // cheap gate; the literal check runs on the full statement
    const at = stmtAt(context);
    if (!at) return null;
    const r = qualifiedCompletions({
      stmtText: at.text, cursor: at.cursor, ns: cfg.schema, defaultSchema: cfg.defaultSchema,
      triggerTable: props.triggerTable, docText: at.doc, docCursor: context.pos,
    });
    if (!r) return null;
    return { from: at.start + r.from, options: r.options.map((c) => ({ ...c, boost: 3 })), validFor: /^[\w$]*$/ };
  };

  // In a TABLE slot (after FROM/JOIN/…) offer the current-DB tables pre-aliased
  // as `tablename <alias>` so columns can be qualified. Boosted above lang-sql's
  // plain table completion, so accepting (Enter/Tab) gives you the aliased form —
  // including the FIRST table, which is why we don't gate on "is there a JOIN
  // yet" (you type the first table before the JOIN exists). We insert PLAIN text
  // with the cursor AFTER the alias (not a snippet tab-stop), so one keypress
  // accepts it and you keep typing; the short alias is still there to edit by
  // hand if you want a different one. lang-sql still offers the bare name below.
  const tableSource = (context) => {
    const cfg = latestSchemaCfg;
    if (!cfg || !cfg.schema) return null;
    const word = context.matchBefore(/[\w]*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    const before = context.state.sliceDoc(Math.max(0, word.from - 4000), word.from);
    if (/\.\s*$/.test(before) || before.endsWith('@')) return null; // qualified (db.table) or @var → not here
    if (routineCompletionMode(before) !== 'none') return null; // table slots only
    const stmt = statementAtCursor(context.state.doc.toString(), context.pos);
    if (!stmt) return null;
    // `taken` = aliases + table names already in the statement, so the alias we
    // generate never clashes with an existing one (duplicates get a numeric
    // suffix, b → b2). Every suggestion is computed against the same `taken`;
    // only the one you accept lands in the query, so two suggestions sharing a
    // letter is harmless — the moment one is inserted the next completion sees it.
    const { taken } = aliasContext(stmt.text);
    const tables = (cfg.defaultSchema && cfg.schema[cfg.defaultSchema]) || null;
    if (!tables) return null;
    const options = [];
    for (const name of Object.keys(tables)) {
      const alias = tableAlias(name, taken);
      options.push({
        label: name,
        detail: `AS ${alias}`,
        type: 'class',
        boost: 2,
        apply: `${name} ${alias}`,
      });
    }
    if (!options.length) return null;
    return { from: word.from, options, validFor: /^[\w]*$/ };
  };

  // After `… JOIN <table> [alias] ON `, suggest the FK-based join condition(s)
  // linking the just-joined table to the others already in the statement. Reads
  // FK edges from the schema index (migration-040 payload). Gated on a warm
  // cache so a cold/partial schema never offers a wrong condition.
  const joinSource = (context) => {
    const cfg = latestSchemaCfg;
    if (!cfg || !latestWarm || !latestIndex) return null;
    const word = context.matchBefore(/[\w]*/);
    if (!word) return null;
    const before = context.state.sliceDoc(Math.max(0, word.from - 200), word.from);
    if (/[.@]\s*$/.test(before)) return null;   // qualified (x.y) / @var → not here
    if (!/\bon\s+$/i.test(before)) return null; // only immediately after `… ON `
    const stmt = statementAtCursor(context.state.doc.toString(), context.pos);
    if (!stmt) return null;
    const refs = fromTables(stmt.text);
    if (refs.length < 2) return null;
    const joined = refs[refs.length - 1]; // the table this ON belongs to
    const conds = joinConditions(latestIndex, cfg.defaultSchema, joined, refs.slice(0, -1));
    if (!conds.length) return null;
    return {
      from: word.from,
      options: conds.map((c) => ({ label: c, type: 'property', boost: 80, detail: 'FK join' })),
      validFor: /^[\w. =]*$/,
    };
  };

  // On `@`, offer session variables (form/import @v… names, supplied by the
  // parent) FIRST, plus the routine's own parameters + local DECLAREs parsed from
  // the document. Session vars keep the `@` (label `@vFoo`); params/locals are
  // shown as `@name` so they match the `@` query but `apply` the bare name (MySQL
  // params aren't @-prefixed). This is the only source that fires on an @-token
  // (the schema sources bail when the word is preceded by `@`), so it ranks above
  // everything by construction.
  const atVarSource = (context) => {
    const word = context.matchBefore(/@[\w]*/);
    if (!word) return null;
    const options = [];
    const seen = new Set();
    for (const v of (props.sessionVars?.() || [])) {
      const label = String(v).startsWith('@') ? String(v) : '@' + v;
      if (seen.has(label)) continue;
      seen.add(label);
      options.push({ label, type: 'variable', boost: 99, detail: 'session var' });
    }
    for (const l of extractRoutineLocals(context.state.doc.toString())) {
      const label = '@' + l.name;
      if (seen.has(label)) continue;
      seen.add(label);
      options.push({
        label, apply: l.name, type: l.kind === 'param' ? 'type' : 'variable',
        boost: l.kind === 'param' ? 60 : 50, detail: l.kind === 'param' ? 'parameter (no @)' : 'local (no @)',
      });
    }
    if (!options.length) return null;
    return { from: word.from, options, validFor: /^@[\w]*$/ };
  };

  // Bulk SQL Runner routing-prefix completion (only when `bracketComplete` is
  // supplied — a { connections(): [{id,name}], databases(name): Promise<string[]> }).
  //   • Inside an unclosed `[…`  → connection names (accepting inserts `Name].`
  //     and re-opens the popup so databases follow).
  //   • After `[Conn].<prefix>`  → that connection's databases (fetched async).
  const bracketSource = async (context) => {
    const bc = props.bracketComplete;
    if (!bc) return null;
    const before = context.state.sliceDoc(0, context.pos);
    const lastOpen = before.lastIndexOf('[');
    const lastClose = before.lastIndexOf(']');
    // 1) typing a connection name inside an unclosed bracket on this line
    if (lastOpen > lastClose && !before.slice(lastOpen).includes('\n')) {
      const conns = bc.connections() || [];
      if (!conns.length) return null;
      const from = lastOpen + 1;
      const closeAhead = context.state.sliceDoc(context.pos, context.pos + 1) === ']';
      return {
        from,
        to: context.pos,
        options: conns.map((c) => ({
          label: c.name,
          type: 'class',
          apply: (view, _c, f, t) => {
            const insert = closeAhead ? c.name : c.name + '].';
            view.dispatch({ changes: { from: f, to: t, insert }, selection: { anchor: f + insert.length } });
            if (!closeAhead) queueMicrotask(() => startCompletion(view)); // → offer databases
          },
        })),
        validFor: /^[^\]\n]*$/,
      };
    }
    // Levels after the closed `[Conn]` — checked most-qualified first, since the
    // regexes are mutually exclusive by the number of dotted segments. An async
    // provider that's absent (e.g. an older host) simply leaves that level off.
    // 2) columns after `[Conn].db.table.`
    const mc = /\[([^\]\n]+)\]\.([A-Za-z0-9_$]+)\.([A-Za-z0-9_$]+)\.([A-Za-z0-9_$]*)$/.exec(before);
    if (mc && bc.columns) {
      const prefix = mc[4];
      let cols = [];
      try { cols = await bc.columns(mc[1].trim(), mc[2], mc[3]); } catch { cols = []; }
      if (!cols || !cols.length) return null;
      return {
        from: context.pos - prefix.length,
        options: cols.map((c) => ({ label: c, type: 'property' })),
        validFor: /^[A-Za-z0-9_$]*$/,
      };
    }
    // 3) tables after `[Conn].db.`
    const mt = /\[([^\]\n]+)\]\.([A-Za-z0-9_$]+)\.([A-Za-z0-9_$]*)$/.exec(before);
    if (mt && bc.tables) {
      const prefix = mt[3];
      let tbls = [];
      try { tbls = await bc.tables(mt[1].trim(), mt[2]); } catch { tbls = []; }
      if (!tbls || !tbls.length) return null;
      return {
        from: context.pos - prefix.length,
        options: tbls.map((t) => ({ label: t, type: 'type' })),
        validFor: /^[A-Za-z0-9_$]*$/,
      };
    }
    // 4) databases after `[Conn].`
    const m = /\[([^\]\n]+)\]\.([A-Za-z0-9_$]*)$/.exec(before);
    if (m) {
      const connName = m[1].trim();
      const prefix = m[2];
      let dbs = [];
      try { dbs = await bc.databases(connName); } catch { dbs = []; }
      if (!dbs || !dbs.length) return null;
      return {
        from: context.pos - prefix.length,
        options: dbs.map((d) => ({ label: d, type: 'namespace' })),
        validFor: /^[A-Za-z0-9_$]*$/,
      };
    }
    return null;
  };

  // Ctrl+Shift+E: hand the statement under the cursor (its FROM/JOIN tables) to
  // the parent's column-expander modal, with a cursor-bound insert callback.
  const expandColumns = (v) => {
    if (!props.onExpandColumns) return false;
    const doc = v.state.doc.toString();
    const sel = v.state.selection.main;
    const head = sel.head;
    const stmt = statementAtCursor(doc, head);
    // Any highlighted text → the column-expander pre-selects the matching table.
    const selectedText = sel.from !== sel.to ? v.state.sliceDoc(sel.from, sel.to) : '';
    props.onExpandColumns({
      statementText: stmt ? stmt.text : '',
      fromTables: stmt ? fromTables(stmt.text) : [],
      selectedText,
      insert: (text) => {
        const pos = v.state.selection.main.head;
        v.dispatch({ changes: { from: pos, insert: text }, selection: { anchor: pos + text.length } });
        v.focus();
      },
    });
    return true;
  };

  onMount(() => {
    // Sizing: a fixed `height` (default), OR — when `maxHeight` is given — grow
    // with the content up to that cap (with a small floor), so a short viewer
    // doesn't reserve a tall empty box.
    // Font size is applied via fontConf (Ctrl+wheel zoom), not here.
    const rootStyle = props.maxHeight
      ? { maxHeight: props.maxHeight, minHeight: props.minHeight ?? '80px' }
      : { height: props.height ?? '280px' };

    view = new EditorView({
      state: EditorState.create({
        doc: props.value ?? '',
        extensions: [
          // Editing helpers
          history(),
          drawSelection(),
          bracketMatching(),
          highlightActiveLine(),
          lineNumbers(),
          // Multiple cursors / column editing: Alt-drag makes a rectangular
          // (multi-line) selection; selectNextOccurrence (Mod-Alt-d below) adds a
          // cursor at the next match of the current word/selection.
          EditorState.allowMultipleSelections.of(true),
          rectangularSelection(),
          crosshairCursor(),
          // Code folding — fold gutter arrows + the fold keymap (Ctrl/Cmd-Shift-[ / ]).
          codeFolding(),
          foldGutter(),
          // Read-only viewer mode: block edits + remove the caret, but keep the
          // text selectable (so it can be copied) and fully highlighted.
          ...(props.readOnly ? [EditorState.readOnly.of(true), EditorView.editable.of(false)] : []),
          // Wrap long lines instead of scrolling horizontally — in a compartment
          // so a toolbar toggle can flip it without rebuilding the editor.
          wrapConf.of(wrapExt()),
          // Editor font size (Ctrl/Cmd+wheel zoom), shared via the editor.fontSize pref.
          fontConf.of(fontExt()),
          // Known-column set + global rename dictionary for the format-time re-caser.
          colsConf.of(colsFacetFor(columnCaseMap())),
          // Hover a clipped line (wrap off) to read it in full.
          lineHoverTooltip,
          routineHoverTooltip,
          schemaHoverTooltip,
          // Format binding first so it wins; then completion accept (Tab/Enter),
          // then the usual editing keymaps. Tab accepts an open completion and
          // otherwise falls through to indent; completionKeymap's Enter accepts
          // when the popup is open and otherwise inserts a newline.
          keymap.of([
            { key: 'Mod-Shift-f', preventDefault: true, run: formatEditor },
            { key: 'Shift-Alt-f', preventDefault: true, run: formatEditor },
            { key: 'Shift-Alt-i', preventDefault: true, run: (v) => formatEditor(v, 'indent') },
            { key: 'Mod-Shift-e', preventDefault: true, run: expandColumns },
            { key: 'Mod-d', preventDefault: true, run: duplicateLineDown },
            // Multi-cursor: add a cursor at the next occurrence of the selection
            // (VSCode Ctrl-D). Mod-d is taken by duplicate-line, so use Mod-Alt-d.
            { key: 'Mod-Alt-d', preventDefault: true, run: selectNextOccurrence },
            // F2: rename the identifier at the caret across the buffer (occurrence
            // rename — editor text only, not the database).
            { key: 'F2', preventDefault: true, run: startRename },
            // Paste newline-delimited clipboard text as a comma list (also in the
            // query-editor right-click menu). Alt+V plain, Alt+Q single-quoted,
            // Alt+Shift+Q double-quoted.
            { key: 'Mod-Alt-v', preventDefault: true, run: (v) => { pasteCommaIntoView(v, ''); return true; } },
            { key: 'Mod-Alt-q', preventDefault: true, run: (v) => { pasteCommaIntoView(v, "'"); return true; } },
            { key: 'Mod-Alt-Shift-q', preventDefault: true, run: (v) => { pasteCommaIntoView(v, '"'); return true; } },
          ]),
          // Surround the selection with quotes: typing ' or " while text is
          // selected wraps it (and keeps the inner text selected) instead of
          // replacing it. With no selection, the quote inserts normally.
          EditorView.inputHandler.of((view, _from, _to, text) => {
            if ((text !== "'" && text !== '"') || view.state.readOnly) return false;
            const { state } = view;
            if (state.selection.ranges.every((r) => r.empty)) return false;
            const ch = text;
            view.dispatch(state.changeByRange((range) => range.empty
              ? { changes: { from: range.from, insert: ch }, range: EditorSelection.cursor(range.from + 1) }
              : {
                  changes: [{ from: range.from, insert: ch }, { from: range.to, insert: ch }],
                  range: EditorSelection.range(range.from + 1, range.to + 1),
                }));
            return true;
          }),
          keymap.of([{ key: 'Tab', run: acceptCompletion }]),
          keymap.of(completionKeymap),
          // Find/replace panel (Ctrl/Cmd+F). Lower precedence than our keymap
          // above, so Mod-D stays "duplicate line" (search's Mod-D / selectNext
          // is shadowed). F3 / Mod-G / Mod-Shift-G cycle matches.
          keymap.of(searchKeymap),
          keymap.of(foldKeymap),
          keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),

          // SQL language (schema-aware completion via the compartment) + autocomplete
          // + one-dark theme (includes syntax highlighting + popup styling).
          sqlConf.of(sqlExtension(props.schema?.())),
          // Built-in find/replace panel (top), opened by the searchKeymap above.
          search({ top: true }),
          autocompletion({ activateOnTyping: true }),
          // Context-aware column + function/procedure completion, MERGED with
          // lang-sql's table/keyword sources (added via the language's data facet).
          MySQL.language.data.of({ autocomplete: atVarSource }),
          MySQL.language.data.of({ autocomplete: bracketSource }), // [connection].database (Bulk SQL Runner)
          MySQL.language.data.of({ autocomplete: columnSource }),
          MySQL.language.data.of({ autocomplete: qualifiedSource }), // alias. / table. / db. / NEW. → scope-aware
          MySQL.language.data.of({ autocomplete: functionSource }), // built-in MySQL functions (freq-ranked)
          MySQL.language.data.of({ autocomplete: routineSource }),
          MySQL.language.data.of({ autocomplete: tableSource }),
          MySQL.language.data.of({ autocomplete: joinSource }),
          MySQL.language.data.of({ autocomplete: snippetSource }),
          // Conservative schema linter (warm-cache gated; see sqlLinter above).
          sqlLinter,
          // F2 rename panel state (renders a rename bar when active).
          renameField,
          // Signature help (tooltip showing the call's signature + active arg).
          sigHelpField,
          themeConf.of(editorThemeExt()),

          // Match the app's monospace font + the focus glow / bracket accents.
          // NOTE: the selection shade lives in global.css
          // (`.cm-editor … .cm-selectionBackground { !important }`, driven by the
          // theme-aware --cm-sel vars) — CodeMirror's base focused-selection rule
          // out-specifies anything an EditorView.theme can register.
          EditorView.theme({
            '&': rootStyle,
            '.cm-scroller': { overflow: 'auto', fontFamily: 'var(--font-mono, "Fira Mono", monospace)' },
            // Subtle border glow on focus using app accent colour
            '&.cm-focused': { outline: 'none', boxShadow: '0 0 0 2px color-mix(in srgb, var(--accent) 35%, transparent)' },
            // Matched bracket under the caret — outline its pair.
            '.cm-matchingBracket': {
              backgroundColor: 'color-mix(in srgb, var(--accent) 22%, transparent)',
              outline: '1px solid color-mix(in srgb, var(--accent) 60%, transparent)',
              borderRadius: '2px',
            },
            '.cm-nonmatchingBracket': { color: 'var(--danger)' },
          }),

          // Report changes back to SolidJS; open the dropdown on a relevant space;
          // auto-format on a typed ';' when opted in.
          EditorView.updateListener.of((update) => {
            if (!update.docChanged) return;
            props.onInput?.(update.state.doc.toString());
            let typedSemicolon = false, typedSpace = false, typedRoute = false;
            update.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
              const s = inserted.toString();
              if (s.length <= 2 && s.includes(';')) typedSemicolon = true; // a typed ';', not a paste
              if (s === ' ') typedSpace = true;                            // a single typed space
              if (s === '[' || s === '.') typedRoute = true;              // routing-prefix triggers
            });
            // Defer so we never dispatch a transaction from inside the update.
            if (typedSpace) queueMicrotask(() => maybeOpenOnSpace(update.view));
            if (props.bracketComplete && typedRoute) queueMicrotask(() => startCompletion(update.view));
            if (props.autoFormat && typedSemicolon) queueMicrotask(() => autoFormatOnSemicolon(update.view));
          }),
        ],
      }),
      parent: container,
    });

    props.onViewReady?.(view);
    if (props.autofocus) queueMicrotask(() => view.focus()); // tab opened already active

    // Ctrl/Cmd + mouse-wheel zooms the editor font. Wheel listeners are passive
    // by default (so preventDefault is ignored and the page would zoom); attach a
    // manual non-passive one so we can preventDefault and nudge the shared
    // editor.fontSize pref (which clamps to its [9,28] range). When Ctrl/Cmd
    // isn't held we do nothing → normal scroll.
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setEditorFontSize(editorFontSize() + (e.deltaY < 0 ? 1 : -1));
    };
    view.scrollDOM.addEventListener('wheel', onWheel, { passive: false });
    onCleanup(() => view.scrollDOM.removeEventListener('wheel', onWheel));

    // Right-click → host-defined context menu (Find usages, Add result title,
    // anything else the parent wants to wire). When the host provides
    // onContextMenu we suppress the browser's native menu and call back with
    // the event + view. The host typically:
    //   1) reads e.clientX/Y → view.posAtCoords for the document position
    //   2) computes the statement + ident at that position
    //   3) shows its own ContextMenu component at the click coords
    // No host wired ⇒ browser default (copy/paste) survives.
    const onCtxMenu = (e) => {
      if (typeof props.onContextMenu !== 'function') return;
      e.preventDefault();
      props.onContextMenu(e, view);
    };
    view.dom.addEventListener('contextmenu', onCtxMenu);
    onCleanup(() => view.dom.removeEventListener('contextmenu', onCtxMenu));
  });

  // Autofocus: drop the caret into the editor when `autofocus` is set. Tracks the
  // prop so it re-fires when a tab is re-activated, not just at mount. onMount ran
  // before this effect's first pass, so `view` exists; queueMicrotask defers past
  // the current render so the (possibly just-revealed) DOM can take focus.
  createEffect(() => {
    if (props.autofocus && view) queueMicrotask(() => view.focus());
  });

  // Re-apply the font size live when the shared editor.fontSize pref changes
  // (this editor's own Ctrl+wheel, or another editor's).
  createEffect(() => {
    editorFontSize(); // track the pref so the effect re-runs on zoom
    if (view) view.dispatch({ effects: fontConf.reconfigure(fontExt()) });
  });

  // Swap the editor's dark/light theme live when the app theme changes.
  createEffect(() => {
    theme(); // track
    if (view) view.dispatch({ effects: themeConf.reconfigure(editorThemeExt()) });
  });

  // Reconfigure the SQL config when the completion schema arrives or the current
  // database changes (reads props.schema() so it tracks reactively).
  createEffect(() => {
    const cfg = props.schema?.();
    const caseMap = columnCaseMap(); // track the global dictionary → re-cases live after a save
    const schemaChanged = cfg !== latestSchemaCfg; // this effect also fires on caseMap-only changes
    latestSchemaCfg = cfg;
    latestRoutineOpts = routineCompletions(cfg?.routines);
    latestColumnSet = columnSetFrom(cfg);
    // The FK/known-table index only depends on the schema payload — skip the
    // rebuild when only the rename dictionary (caseMap) changed.
    if (schemaChanged) {
      latestIndex = buildIndex(cfg?.databases);
      latestWarm = schemaWarm(cfg);
    }
    if (view) {
      view.dispatch({ effects: [
        sqlConf.reconfigure(sqlExtension(cfg)),
        colsConf.reconfigure(colsFacetFor(caseMap)),
      ] });
      // Re-lint now that the schema changed (the linter otherwise only re-runs
      // on edits, so squiggles would lag a just-arrived/refreshed cache).
      forceLinting(view);
    }
  });

  // Flip line wrapping live when the toolbar toggle (props.lineWrap) changes.
  createEffect(() => {
    latestLineWrap = !!props.lineWrap; // keep the hover-tooltip gate in sync
    if (view) view.dispatch({ effects: wrapConf.reconfigure(wrapExt()) });
  });

  // Sync externally-driven value changes (e.g. "Load / Generate" fills the script).
  // Guard prevents infinite loop: don't dispatch if already in sync.
  createEffect(() => {
    const incoming = props.value ?? '';
    if (view && view.state.doc.toString() !== incoming) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: incoming },
      });
    }
  });

  onCleanup(() => view?.destroy());

  return <div ref={container} class="cm-sql-container" />;
}
